import React from 'react';
import { css } from '@emotion/react';
import {
    CHANGE_LOCATION,
    YOUR_DETECTED_LOCATION,
    YOUR_SELECTED_LOCATION,
    LOCAL_CITY_LOCATION,
    AVERAGE_RATE_THRESHOLD,
} from '../../../constants';
import { useLocator } from '../../../contexts/franchise/locator-provider';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import RatingSummary from './rating-summary';
import { borderRadius, breakpoints } from '../../primitives/tokens';
import Container from '../../primitives/grid/container';
import { colors } from '../../primitives/colors';
import PhoneNumber from '../../primitives/phone-number';
import { StyledButtonAsLink } from '../../primitives/styled-button';
import Skeleton from '../../primitives/skeleton';
import StyledPillButton from '../../primitives/styled-pill-button';
import ChangeLocationDrawer from './change-location-drawer';
import Typography from '../../primitives/typography';
import LeaveUsAReview from './leave-us-a-review';
import Link from '../../primitives/link';
import formatPhoneLink from '../../utils/format-phone-link';
import FadeTransition from '../../animations/transitions/fade-transition';

const styles = {
    nearestWrapInner: css({
        boxShadow: '2px 2px 10px 3px rgba(0, 0, 0, 0.135359)',
        borderRadius: borderRadius.small,
        backgroundColor: colors.shadesWhite,
        padding: '24px 40px',
        [breakpoints.tb]: {
            padding: '24px',
            width: '100%',
            boxShadow: 'none',
            borderBottom: `1px solid ${colors.grey200}`,
            borderTop: `1px solid ${colors.grey200}`,
            borderRadius: '0',
        },
    }),
    phoneMobile: css({
        display: 'none',
        [breakpoints.tb]: {
            display: 'inline-flex',
        },
    }),
    phoneDesktopWrapper: css({
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        [breakpoints.tb]: {
            display: 'none',
        },
    }),
    nationalCallCenterText: css({ marginBottom: '8px' }),
    phoneButton: css({ minWidth: '256px' }),
    layoutWrapper: css({
        display: 'flex',
        gap: '24px',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '128px',
        [breakpoints.tb]: {
            minHeight: 'unset',
            alignItems: 'flex-start',
            gap: '0',
            flexDirection: 'column',
        },
        [breakpoints.mb]: {
            alignItems: 'flex-start',
            flexDirection: 'column',
        },
    }),
    headerTypography: css({
        letterSpacing: '0.08rem',
        marginBottom: '12px',
    }),
    firstBlock: css({
        marginBottom: '16px',
        [breakpoints.tb]: {
            marginBottom: '12px',
        },
        a: {
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            textDecorationThickness: '1px',
            [breakpoints.tb]: {
                textDecorationThickness: '1px',
            },
        },
    }),
    skeletonFirstLine: css({
        width: '200px',
        marginBottom: '6px',
    }),
    secondLine: css({ marginTop: '4px' }),
    skeletonSecondLine: css({ width: '300px' }),
    changeLocationWrapper: css({ marginTop: '12px' }),
    changeLocationInner: css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        transition: '0.8s',
        [breakpoints.tb]: {
            gap: '8px',
        },
    }),
    skeletonChangeLocation: css({ width: '100px' }),
    rightColWrapper: css({
        display: 'flex',
        gap: '24px',
        alignItems: 'center',
        width: 'min(54%, 620px)',
        justifyContent: 'space-between',
        [breakpoints.tb]: {
            flexDirection: 'column',
            width: '100%',
        },
        [breakpoints.mb]: {
            width: '100%',
            alignItems: 'flex-start',
        },
    }),
    ratingPhoneWrapper: css({
        display: 'flex',
        gap: '8px',
        width: '100%',
        flexDirection: 'column',
        [breakpoints.tb]: {
            justifyContent: 'space-around',
            flexDirection: 'row',
        },
        [breakpoints.mb]: {
            flexDirection: 'column',
        },
    }),
    reviewWrapper: css({
        display: 'flex',
        gap: '4px',
        alignItems: 'flex-start',
        flexDirection: 'column',
        [breakpoints.tb]: {
            gap: '5px',
            alignItems: 'center',
        },
        [breakpoints.mb]: {
            alignItems: 'flex-start',
        },
    }),
    reviewHeading: css({ lineHeight: '1.2' }),
    franchiseNameText: css({
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: '600px', // Force wrapping after this width
        display: 'block',
    }),
};

type NearestWrapProps = {
    children: React.ReactNode;
    className?: string;
};

type RenderPhoneNumberProps = {
    phone?: string | undefined;
    franchise?: { tempOutOfService?: boolean };
    isMobileView?: boolean;
    isMicrosite?: boolean;
};

const NearestWrap = ({ children, className }: NearestWrapProps): JSX.Element => (
    <Container removeAt="tb" className={className}>
        <div css={styles.nearestWrapInner}>{children}</div>
    </Container>
);

const RenderPhoneNumber = ({ phone, franchise, isMobileView, isMicrosite }: RenderPhoneNumberProps): JSX.Element | null => {
    if (!phone) return null;
    const formattedPhone = formatPhoneLink(phone);

    if (isMobileView) {
        if (phone && !isMicrosite) {
            return (
                <PhoneNumber
                    css={styles.phoneMobile}
                    as={StyledButtonAsLink}
                    to={`tel:${formattedPhone}`}
                >
                    {formattedPhone}
                </PhoneNumber>
            );
        }
        return null;
    }

    return (
        <div css={styles.phoneDesktopWrapper}>
            {franchise?.tempOutOfService && phone?.includes('SERVPRO') && (
                <h2 css={styles.nationalCallCenterText}>National Call Center</h2>
            )}
            <PhoneNumber css={styles.phoneButton} as={StyledButtonAsLink} to={`tel:${formattedPhone}`}>
                {formattedPhone}
            </PhoneNumber>
        </div>
    );
};

const Nearest = (): JSX.Element => {
    const { geo, franchise: locatorFranchise, locationChangedByUser, isNationalCallCenter, loadingFirst: locatorLoadingFirstResult } = useLocator();
    const { isMicrosite, isLocalCityPage, isMicrositeHomepage, ...microsite } = useMicrosite();
    const [open, setOpen] = React.useState(false);

    const franchise = microsite?.franchise || locatorFranchise?.data;
    const name = franchise?.name;
    const averageRating = franchise?.averageRating;
    const permalink = franchise?.permalink;
    const reviewCount = franchise?.reviewCount;
    const phone = franchise?.mainPhone;
    const reviewLink = microsite?.franchise?.googleReviewLink;

    // This is a more accurate loading state, as 'loading' from useLocator will take into account -all- 5 franchise options being loaded.
    const loading = isMicrosite ? false : (!name || !franchise || !phone || locatorLoadingFirstResult);
    const shouldDisplayReview = !loading && reviewCount !== 0 && !!averageRating && averageRating >= AVERAGE_RATE_THRESHOLD;

    const locationInfo = React.useMemo(() => {
        const getLocationHeaderText = (): string => {
            if (isMicrosite) return 'Here to Help® 24/7';
            if (isLocalCityPage) return LOCAL_CITY_LOCATION;
            return locationChangedByUser ? YOUR_SELECTED_LOCATION : YOUR_DETECTED_LOCATION;
        };

        const getFirstLineText = (): string | undefined => {
            if (isMicrosite) return name;
            if (isLocalCityPage) return `${microsite?.targetCity?.city || microsite?.franchise?.address?.city}, ${microsite?.targetCity?.state}`;
            if (!loading) {
                if (locationChangedByUser) return name;
                if (!locationChangedByUser && !!geo && !!geo.city) {
                    return `${geo?.city}${geo?.stateShort ? `, ${geo.stateShort}` : ''}`;
                }
            }
            return '';
        };

        const getFirstLineLink = (): string | undefined => {
            if (isMicrosite || isLocalCityPage) return undefined;
            return (name && locationChangedByUser) ? permalink : undefined;
        };

        const hasSecondLine = (): boolean => {
            if (isMicrosite) return false;
            if (isLocalCityPage) return true;
            return !(name && locationChangedByUser);
        };

        const getSecondLineText = (): string | undefined => {
            if (isMicrosite) return '';
            if (isLocalCityPage) return name;
            if (!loading) {
                if (!locationChangedByUser && isNationalCallCenter) {
                    return 'For service in your area, please call our National Call Center.';
                }
                if (name && !locationChangedByUser) return name;
            }
            return '';
        };

        const getSecondLineLink = (): string | undefined => {
            if (isMicrosite) return undefined;
            if (isLocalCityPage) return permalink;
            return (name && !locationChangedByUser) ? permalink : undefined;
        };

        return {
            headerText: getLocationHeaderText(),
            firstLine: {
                text: getFirstLineText(),
                link: getFirstLineLink(),
                Component: (() => {
                    if (isMicrositeHomepage) return 'h1';
                    if (getFirstLineLink()) return Link;
                    return 'div';
                })() as React.ElementType,
                InnerComponent: (() => {
                    if (isMicrositeHomepage) return 'div';
                    if (getFirstLineLink()) return 'div';
                    return 'p';
                })() as React.ElementType,
            },
            secondLine: {
                hasText: hasSecondLine(),
                text: getSecondLineText(),
                link: getSecondLineLink(),
                Component: (() => {
                    if (getSecondLineLink()) return Link;
                    return 'div';
                })() as React.ElementType,
                InnerComponent: (() => {
                    if (getSecondLineLink()) return 'div';
                    return 'p';
                })() as React.ElementType,
            },
        };
    }, [isMicrosite, isLocalCityPage, locationChangedByUser, loading, name, geo, permalink, isMicrositeHomepage, microsite, isNationalCallCenter]);

    const FirstLineInnerComponent = locationInfo.firstLine.InnerComponent as React.ElementType;
    const SecondLineInnerComponent = locationInfo.secondLine.InnerComponent as React.ElementType;

    return (
        <NearestWrap>
            <div css={styles.layoutWrapper}>
                <div>
                    <Typography
                        as="p"
                        fontSize={{
                            dt: 'base',
                            tb: 'sm',
                        }}
                        color="primaryOrange"
                        fontWeight="regular"
                        css={styles.headerTypography}
                    >
                        {locationInfo.headerText}
                    </Typography>

                    <div css={styles.firstBlock}>
                        <Typography
                            as={locationInfo.firstLine.Component as React.ElementType}
                            to={locationInfo.firstLine.link}
                            fontSize={{
                                dt: '3xl',
                                tb: 'xl',
                            }}
                            fontWeight="medium"
                        >
                            <Skeleton css={styles.skeletonFirstLine}>
                                {locationInfo.firstLine.text && (
                                    <FirstLineInnerComponent css={styles.franchiseNameText}>
                                        {locationInfo.firstLine.text}
                                    </FirstLineInnerComponent>
                                )}
                            </Skeleton>
                        </Typography>

                        {locationInfo.secondLine.hasText && (
                            <Typography
                                as={locationInfo.secondLine.Component as React.ElementType}
                                to={locationInfo.secondLine.link}
                                fontSize={{
                                    dt: 'xl',
                                    tb: 'base',
                                }}
                                css={styles.secondLine}
                            >
                                <Skeleton css={styles.skeletonSecondLine}>
                                    {locationInfo.secondLine.text && (
                                        <SecondLineInnerComponent css={styles.franchiseNameText}>
                                            {locationInfo.secondLine.text || null}
                                        </SecondLineInnerComponent>
                                    )}
                                </Skeleton>
                            </Typography>
                        )}

                        {(!isLocalCityPage && !isMicrosite) && (
                            <div className="change-location-open-cta" css={styles.changeLocationWrapper}>
                                <Skeleton css={styles.skeletonChangeLocation}>
                                    {!loading && (
                                        <div css={styles.changeLocationInner}>
                                            <span>Need help elsewhere?</span>
                                            <StyledPillButton onClick={() => setOpen(true)}>
                                                {CHANGE_LOCATION}
                                            </StyledPillButton>
                                        </div>
                                    )}
                                </Skeleton>
                            </div>
                        )}
                    </div>
                </div>

                <div css={styles.rightColWrapper}>
                    <div css={styles.ratingPhoneWrapper}>
                        <FadeTransition shouldChange={shouldDisplayReview.toString()}>
                            <div css={styles.reviewWrapper}>
                                {shouldDisplayReview && (
                                    <Typography
                                        fontSize={{ dt: 'xl', tb: 'lg' }}
                                        css={styles.reviewHeading}
                                        fontWeight="medium"
                                        color="primaryOrange"
                                    >
                                        {isMicrosite ? 'Our Google Reviews' : '24/7 Emergency Service'}
                                    </Typography>
                                )}
                                <RenderPhoneNumber phone={phone} isMobileView />
                                {shouldDisplayReview && typeof averageRating === 'number' && typeof reviewCount === 'number' && (
                                    <RatingSummary rating={averageRating} count={reviewCount} />
                                )}
                            </div>
                        </FadeTransition>

                        {reviewLink && !isLocalCityPage && (
                            <div>
                                <LeaveUsAReview reviewLink={reviewLink} />
                            </div>
                        )}
                    </div>

                    <RenderPhoneNumber phone={phone} franchise={franchise} isMicrosite={isMicrosite} />
                </div>
            </div>

            <ChangeLocationDrawer visible={open} onClose={() => setOpen(false)} />
        </NearestWrap>
    );
};

export default Nearest;
